import { useQuery } from "@tanstack/react-query"
import { useSearchParams } from "react-router-dom"
import { ArrowRight, Footprints, Lightbulb, Map, Phone, Repeat, Target } from "lucide-react"
import { PageHeader } from "@/components/portal/PageHeader"
import ProjektplanPage from "@/pages/ProjektplanPage"
import RueckkopplungPage from "@/pages/RueckkopplungPage"
import TelefonbotRueckkopplungPage from "@/pages/TelefonbotRueckkopplungPage"
import VorschlagsqualitaetPage from "@/pages/VorschlagsqualitaetPage"
import WegePage from "@/pages/WegePage"
import WerbeAbsenderPage from "@/pages/WerbeAbsenderPage"

/**
 * „Verbessern" — alles, was nur unsere internen Abläufe besser macht, unter
 * einem Menüpunkt (Paul 11.09.2026: „diese ganzen Sachen, die nur unsere
 * internen Prozesse verbessern — Wege / Vorschlagsqualität / Rückkopplung —
 * sollten unter einem Menüpunkt zusammengefasst werden und verständlich
 * aufbereitet").
 *
 * Der Überblick erklärt in Klartext, was die drei Seiten sind, welche Frage
 * jede beantwortet und was ein Mensch dort zu tun hat. Die drei Seiten selbst
 * bleiben, wie sie sind — als Reiter. Alte Links (/wege, /vorschlagsqualitaet,
 * /rueckkopplung) landen hier auf dem passenden Reiter.
 */

export type Reiter = "ueberblick" | "projektplan" | "rueckkopplung" | "telefonbot" | "vorschlagsqualitaet" | "wege" | "werbe-absender"
const REITER: { key: Reiter; label: string }[] = [
  { key: "ueberblick", label: "Überblick" },
  { key: "projektplan", label: "Projektplan" },
  { key: "rueckkopplung", label: "Rückkopplung" },
  { key: "telefonbot", label: "Telefonbot" },
  { key: "vorschlagsqualitaet", label: "Vorschlagsqualität" },
  { key: "wege", label: "Wege" },
  { key: "werbe-absender", label: "Werbe-Absender" },   // #172
]

export function reiterAus(wert: string | null): Reiter {
  return (REITER.find((r) => r.key === wert)?.key ?? "ueberblick")
}

type Kennzahlen = {
  ausgaenge?: { je_ausgang: Record<string, number>; plaene_gesamt: number }
  no_touch_quote?: number | null; no_touch_basis?: number
  deutung?: { gedeutet: number; unklar: number }
}

function Zahl({ wert, einheit }: { wert: number | string | null | undefined; einheit?: string }) {
  return <span className="text-[22px] font-semibold tabular-nums">{wert ?? "—"}{einheit && wert != null ? <span className="text-[13px] font-normal text-muted-foreground"> {einheit}</span> : null}</span>
}

function Ueberblick({ oeffnen }: { oeffnen: (r: Reiter) => void }) {
  const kenn = useQuery<{ kennzahlen: Kennzahlen }>({
    queryKey: ["verbessern-kennzahlen"],
    queryFn: async () => {
      const r = await fetch("/api/v1/vorschlagsqualitaet?tage=30", { credentials: "include" })
      if (!r.ok) throw new Error(`HTTP ${r.status}`); return r.json()
    },
  })
  const entwuerfe = useQuery<{ regeln: unknown[] }>({
    queryKey: ["verbessern-entwuerfe"],
    queryFn: async () => {
      const r = await fetch("/api/v1/rueckkopplung/regeln?status=entwurf", { credentials: "include" })
      if (!r.ok) throw new Error(`HTTP ${r.status}`); return r.json()
    },
  })
  const bot = useQuery<{ kennzahlen?: { anrufe: number; rueckrufe: number }; vorschlaege?: { status: string }[] }>({
    queryKey: ["verbessern-telefonbot"],
    queryFn: async () => {
      const r = await fetch("/api/v1/rueckkopplung/voice?tage=30", { credentials: "include" })
      if (!r.ok) throw new Error(`HTTP ${r.status}`); return r.json()
    },
  })
  const plan = useQuery<{ bausteine: { status: string }[] }>({
    queryKey: ["strategie"],
    queryFn: async () => {
      const r = await fetch("/api/v1/strategie", { credentials: "include" })
      if (!r.ok) throw new Error(`HTTP ${r.status}`); return r.json()
    },
  })
  const botOffen = (bot.data?.vorschlaege || []).filter((x) => x.status === "entwurf").length
  const k = kenn.data?.kennzahlen || {}
  const offen = entwuerfe.data?.regeln?.length ?? 0
  const je = k.ausgaenge?.je_ausgang || {}
  const liegen = je.abgelaufen ?? 0

  return (
    <div className="max-w-[900px]">
      <p className="text-[13.5px] text-muted-foreground mb-5 max-w-[72ch]">
        Der Assistent liest Mails, schlägt Antworten und HERO-Einträge vor und legt sie nach Freigabe an.
        Ob seine Vorschläge gut sind, entscheidet sich daran, was das Büro damit macht. Genau das misst
        dieser Bereich, ohne dass jemand etwas eintragen muss. Ein Mensch entscheidet hier nur über Regeln.
      </p>

      {/* Die Kette in einem Satz */}
      <div className="pp-card p-3 mb-5 text-[12.5px] flex flex-wrap items-center gap-x-2 gap-y-1">
        {["Vorschlag", "Wie endete er?", "Was geschah stattdessen?", "Warum?", "Wochenbericht", "Regel", "Wirkt sie?"].map((s, i, arr) => (
          <span key={s} className="inline-flex items-center gap-2">
            <span className={i === 0 ? "font-medium" : ""}>{s}</span>
            {i < arr.length - 1 && <ArrowRight className="w-3.5 h-3.5 text-muted-foreground" />}
          </span>
        ))}
      </div>

      <div className="grid md:grid-cols-2 gap-3 mb-5">
        <div className="pp-card p-4 flex flex-col gap-2" data-testid="karte-projektplan">
          <div className="flex items-center gap-2 font-semibold text-[14px]"><Map className="w-4 h-4" />Projektplan</div>
          <div className="text-[12.5px] text-muted-foreground">
            <strong className="text-foreground">Frage:</strong> Wo steht die Strategie „Von Assistenz zu Autonomie“, und wirkt jeder Baustein messbar?
          </div>
          <div className="text-[12.5px] text-muted-foreground">
            Dreizehn Bausteine mit Stand aus GitHub, Kennzahl je Baustein mit Baseline und Ziel, Wirkung vorher gegen nachher aus
            einem Wochen-Schnappschuss ohne Personendaten.
          </div>
          <div className="text-[12.5px]"><strong>Hier tun Sie etwas:</strong> Live-Datum setzen, wenn GitHub keins hergibt. Sonst nur lesen.</div>
          <div className="mt-auto pt-2 flex items-baseline justify-between">
            <span><Zahl wert={plan.data ? plan.data.bausteine.filter((b) => b.status === "live" || b.status === "gemessen").length : null} /> <span className="text-[12px] text-muted-foreground">von {plan.data?.bausteine.length ?? 13} Bausteinen live</span></span>
            <button type="button" className="pp-btn pp-btn-sm" onClick={() => oeffnen("projektplan")}>Öffnen</button>
          </div>
        </div>

        <div className="pp-card p-4 flex flex-col gap-2" data-testid="karte-rueckkopplung">
          <div className="flex items-center gap-2 font-semibold text-[14px]"><Repeat className="w-4 h-4" />Rückkopplung</div>
          <div className="text-[12.5px] text-muted-foreground">
            <strong className="text-foreground">Frage:</strong> Warum wurde ein Vorschlag nicht übernommen, und welche Regel würde das ändern?
          </div>
          <div className="text-[12.5px] text-muted-foreground">
            Das System sammelt, was nach jedem Plan passiert ist (Portal, Telefon, HERO), deutet den Grund und
            fasst einmal pro Woche zusammen. Daraus entstehen Regel-Entwürfe.
          </div>
          <div className="text-[12.5px]"><strong>Hier tun Sie etwas:</strong> Entwürfe übernehmen oder verwerfen. Fünf Minuten pro Woche.</div>
          <div className="mt-auto pt-2 flex items-baseline justify-between">
            <span><Zahl wert={offen} /> <span className="text-[12px] text-muted-foreground">offene Entwürfe</span></span>
            <button type="button" className="pp-btn pp-btn-sm" onClick={() => oeffnen("rueckkopplung")}>Öffnen</button>
          </div>
        </div>

        <div className="pp-card p-4 flex flex-col gap-2" data-testid="karte-telefonbot">
          <div className="flex items-center gap-2 font-semibold text-[14px]"><Phone className="w-4 h-4" />Telefonbot</div>
          <div className="text-[12.5px] text-muted-foreground">
            <strong className="text-foreground">Frage:</strong> Warum musste das Büro nach einem Bot-Anruf noch einmal ran, und was am Bot würde das ändern?
          </div>
          <div className="text-[12.5px] text-muted-foreground">
            Das System legt Bot-Gespräch, Rückruf des Büros und die Antwort des Kunden per SMS-Link nebeneinander, deutet
            den Grund und fasst einmal pro Woche zusammen. Daraus entstehen Vorschläge für den Bot
            {bot.data?.kennzahlen ? ` — zuletzt ${bot.data.kennzahlen.rueckrufe} Rückrufe nach ${bot.data.kennzahlen.anrufe} Bot-Anrufen in 30 Tagen.` : "."}
          </div>
          <div className="text-[12.5px]"><strong>Hier tun Sie etwas:</strong> Vorschlag lesen, als Ticket bestätigen oder verwerfen. Von selbst entsteht kein Ticket.</div>
          <div className="mt-auto pt-2 flex items-baseline justify-between">
            <span><Zahl wert={botOffen} /> <span className="text-[12px] text-muted-foreground">offene Vorschläge</span></span>
            <button type="button" className="pp-btn pp-btn-sm" onClick={() => oeffnen("telefonbot")}>Öffnen</button>
          </div>
        </div>

        <div className="pp-card p-4 flex flex-col gap-2" data-testid="karte-vorschlagsqualitaet">
          <div className="flex items-center gap-2 font-semibold text-[14px]"><Target className="w-4 h-4" />Vorschlagsqualität</div>
          <div className="text-[12.5px] text-muted-foreground">
            <strong className="text-foreground">Frage:</strong> Wie oft liegt der Assistent richtig, und wo korrigiert das Büro ihn?
          </div>
          <div className="text-[12.5px] text-muted-foreground">
            Zählt, wie Pläne endeten und was vor dem Ausführen geändert wurde: abgewählt, ergänzt, umgeschrieben.
            Nur Zahlen, keine Inhalte.
          </div>
          <div className="text-[12.5px]"><strong>Hier tun Sie nichts.</strong> Nur lesen. Die Zahlen speisen den Wochenbericht.</div>
          <div className="mt-auto pt-2 flex items-baseline justify-between">
            <span><Zahl wert={k.no_touch_quote != null ? Math.round(k.no_touch_quote) : null} einheit="%" /> <span className="text-[12px] text-muted-foreground">unverändert übernommen</span></span>
            <button type="button" className="pp-btn pp-btn-sm" onClick={() => oeffnen("vorschlagsqualitaet")}>Öffnen</button>
          </div>
        </div>

        <div className="pp-card p-4 flex flex-col gap-2" data-testid="karte-wege">
          <div className="flex items-center gap-2 font-semibold text-[14px]"><Footprints className="w-4 h-4" />Wege</div>
          <div className="text-[12.5px] text-muted-foreground">
            <strong className="text-foreground">Frage:</strong> Welche Klickfolgen geht das Büro immer wieder, und ginge das kürzer?
          </div>
          <div className="text-[12.5px] text-muted-foreground">
            Zählt Bedienung, nicht Inhalt: welcher Knopf nach welchem. Auffällige Wiederholungen sind Kandidaten für
            einen Knopf weniger.
          </div>
          <div className="text-[12.5px]"><strong>Hier tun Sie nichts.</strong> Nur lesen, bei Bedarf einen Wunsch ins Backlog.</div>
          <div className="mt-auto pt-2 flex items-baseline justify-end">
            <button type="button" className="pp-btn pp-btn-sm" onClick={() => oeffnen("wege")}>Öffnen</button>
          </div>
        </div>
      </div>

      <div className="pp-card p-4 text-[12.5px]">
        <div className="flex items-center gap-2 font-semibold text-[13px] mb-1"><Lightbulb className="w-4 h-4" />Woran Sie sehen, dass es wirkt</div>
        <ul className="list-disc ml-5 space-y-1 text-muted-foreground">
          <li>Der Anteil <strong className="text-foreground">unverändert übernommener</strong> Vorschläge steigt (Vorschlagsqualität).</li>
          <li>Weniger Pläne bleiben <strong className="text-foreground">liegen</strong>: zurzeit {liegen} in 30 Tagen (Rückkopplung, Zahlen dahinter).</li>
          <li>Eine übernommene Regel bekommt nach sieben Tagen ein <strong className="text-foreground">Wirkt / Keine Besserung</strong> (Rückkopplung, übernommene Regeln).</li>
          <li>Deutungen mit <strong className="text-foreground">unklar</strong> werden seltener: zurzeit {k.deutung?.unklar ?? 0} von {k.deutung?.gedeutet ?? 0}.</li>
        </ul>
      </div>
    </div>
  )
}

export default function VerbessernPage() {
  const [params, setParams] = useSearchParams()
  const reiter = reiterAus(params.get("tab"))
  const oeffnen = (r: Reiter) => setParams(r === "ueberblick" ? {} : { tab: r })

  return (
    <div>
      <div className="px-10 pt-8">
        <PageHeader title="Verbessern" />
        <div className="flex gap-1 border-b mb-2" role="tablist">
          {REITER.map((r) => (
            <button
              key={r.key} type="button" role="tab" aria-selected={reiter === r.key}
              onClick={() => oeffnen(r.key)}
              className={`px-3 py-1.5 text-[13px] border-b-2 -mb-px ${reiter === r.key ? "border-foreground font-medium" : "border-transparent text-muted-foreground hover:text-foreground"}`}
            >
              {r.label}
            </button>
          ))}
        </div>
      </div>
      {reiter === "ueberblick" && <div className="px-10 pb-8 pt-4"><Ueberblick oeffnen={oeffnen} /></div>}
      {reiter === "projektplan" && <ProjektplanPage />}
      {reiter === "rueckkopplung" && <RueckkopplungPage />}
      {reiter === "telefonbot" && <TelefonbotRueckkopplungPage />}
      {reiter === "vorschlagsqualitaet" && <VorschlagsqualitaetPage />}
      {reiter === "wege" && <WegePage />}
      {reiter === "werbe-absender" && <WerbeAbsenderPage />}
    </div>
  )
}
